// Libraries
import * as d3 from 'd3';

// MUI
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';

import { ComponentGroup } from './Query';
import { selectVariable } from '../utils/Global';

function AccordionFilters({title, defaultExpanded, children}: {title: string, defaultExpanded: boolean, children: any}) {
    return(
        <ComponentGroup>
            <Accordion defaultExpanded={defaultExpanded} >
                <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                ><h3>{title}</h3>
                </AccordionSummary>
                <AccordionDetails>
                    {children}
                </AccordionDetails>
            </Accordion>
        </ComponentGroup>
    )
}

function Check({k, label, disabled, toggle, setToggle, setValue, marks}: 
               {k: string, label: string, disabled: boolean, toggle: boolean, setToggle: Function, setValue: Function, marks: any[]}) {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToggle(event.target.checked);
        setValue([marks[0].value, marks[1].value] as number[]);
    };

    return (
        <FormControlLabel key={k + "-checkbox"} control={<Checkbox disabled={disabled} checked={toggle} onChange={handleChange} />} label={label} />
    );
}

function FilterGroup({title, children}: {title: string, children: any}) {
    return(
        <div>
            <h4>{title}</h4>
            {children}
        </div>
    )
}

function IndicatorSlider({k, disabled, marks, value, setValue}: 
                         {k: string, disabled: boolean, marks: any[], value: any[], setValue: Function}) {

    const handleChange = (event: Event, newValue: number | number[]) => {
        setValue(newValue as number[]);
    };

    return(
        <div className="SliderPadding">
            <Slider
                key={k + "-slider"}
                disabled={disabled}
                // defaultValue={50}
                value={value}
                onChange={handleChange}
                min={marks[0].value}
                max={marks[1].value}
                marks={marks}
                valueLabelDisplay="on"
                size="medium"
            />
        </div>
    )
}

function ShowSlider({k, label, disabled, toggle, setToggle, marks, value, setValue}: 
                    {k: string, label: string, disabled: boolean, toggle: boolean, setToggle: Function, 
                    marks: any[], value: number[], setValue: Function}) {

    return( 
        <div key={k}>
            <Check k={k} label={label} disabled={disabled} toggle={toggle} setToggle={setToggle} setValue={setValue} marks={marks}/>
            {toggle ?<IndicatorSlider k={k} disabled={disabled} marks={marks} value={value} setValue={setValue}/> : null}
        </div>
    )
}

function EquityIndicatorsFilter({geoLevel, disabled,
                                 showPctBlackCVAPFilter, setShowPctBlackCVAPFilter, 
                                 pctBlackCVAPFilter, setPctBlackCVAPFilter, pctBlackRangeMarks,
                                 showPctHispanicCVAPFilter, setShowPctHispanicCVAPFilter, 
                                 pctHispanicCVAPFilter, setPctHispanicCVAPFilter, pctHispanicRangeMarks,
                                 showPopDensityFilter, setShowPopDensityFilter,
                                 popDensityFilter, setPopDensityFilter, popDensityRangeMarks}: 
                                {geoLevel: string, disabled: boolean,
                                 showPctBlackCVAPFilter: boolean, setShowPctBlackCVAPFilter: Function, 
                                 pctBlackCVAPFilter: number[], setPctBlackCVAPFilter: Function, pctBlackRangeMarks: any[],
                                 showPctHispanicCVAPFilter: boolean, setShowPctHispanicCVAPFilter: Function, 
                                 pctHispanicCVAPFilter: number[], setPctHispanicCVAPFilter: Function, pctHispanicRangeMarks: any[],
                                 showPopDensityFilter: boolean, setShowPopDensityFilter: Function,
                                 popDensityFilter: number[], setPopDensityFilter: Function, popDensityRangeMarks: any[]}) {

    const baseYearPctBlackCVAP = selectVariable.equityIndicator.find((d: any) => d.variable === "baseYearPctCVAPBlack");
    const baseYearPctBlackCVAPLabel = baseYearPctBlackCVAP ? baseYearPctBlackCVAP.descr : '';

    const baseYearPctHispanicCVAP = selectVariable.equityIndicator.find((d: any) => d.variable === "baseYearPctCVAPHispanic");
    const baseYearPctHispanicCVAPLabel = baseYearPctHispanicCVAP ? baseYearPctHispanicCVAP.descr : '';

    const baseYearPopDensity = selectVariable.equityIndicator.find((d: any) => d.variable === "baseYearPopDensity");
    const baseYearPopDensityLabel = baseYearPopDensity ? baseYearPopDensity.descr : '';

    return (
        <FilterGroup title="Equity indicators">
            <ShowSlider
                k={`${geoLevel}-baseYearPctBlackCVAP`}
                label={baseYearPctBlackCVAPLabel}
                disabled={disabled}
                toggle={showPctBlackCVAPFilter}
                setToggle={setShowPctBlackCVAPFilter}
                marks={pctBlackRangeMarks}
                value={pctBlackCVAPFilter}
                setValue={setPctBlackCVAPFilter}
            />
             <ShowSlider
                k={`${geoLevel}-baseYearPctHispanicCVAP`}
                label={baseYearPctHispanicCVAPLabel}
                disabled={disabled}
                toggle={showPctHispanicCVAPFilter}
                setToggle={setShowPctHispanicCVAPFilter}
                marks={pctHispanicRangeMarks}
                value={pctHispanicCVAPFilter}
                setValue={setPctHispanicCVAPFilter}
            />
            <ShowSlider
                k={`${geoLevel}-baseYearPopDensity`}
                label={baseYearPopDensityLabel}
                disabled={disabled}
                toggle={showPopDensityFilter}
                setToggle={setShowPopDensityFilter}
                marks={popDensityRangeMarks}
                value={popDensityFilter}
                setValue={setPopDensityFilter}
            />
        </FilterGroup>
    )
}

function PerCapitaIndicatorsFilter({geoLevel, disabled,
    changeYearCVAPPerPollCountyFilter, setChangeYearCVAPPerPollCountyFilter, changeYearCVAPPerPollCountyMarks, showChangeYearCVAPPerPollCountyFilter, setShowChangeYearCVAPPerPollCountyFilter,
    baseYearCVAPPerPollCountyFilter, setBaseYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyMarks,  showBaseYearCVAPPerPollCountyFilter, setShowBaseYearCVAPPerPollCountyFilter
   }: 
   {geoLevel: string, disabled: boolean,
    changeYearCVAPPerPollCountyFilter: number[], setChangeYearCVAPPerPollCountyFilter: Function, changeYearCVAPPerPollCountyMarks: any[],
    showChangeYearCVAPPerPollCountyFilter: boolean, setShowChangeYearCVAPPerPollCountyFilter: Function,
    baseYearCVAPPerPollCountyFilter: number[], setBaseYearCVAPPerPollCountyFilter: Function, baseYearCVAPPerPollCountyMarks: any[],
    showBaseYearCVAPPerPollCountyFilter: boolean, setShowBaseYearCVAPPerPollCountyFilter: Function,
    }) {

    const changeYearCVAPPerPoll = selectVariable.indicator.find((d: any) => d.variable === "changeYearCVAPPerPoll");
    const changeYearCVAPPerPollLabel = changeYearCVAPPerPoll ? changeYearCVAPPerPoll.descr : '';

    const baseYearCVAPPerPoll = selectVariable.indicator.find((d: any) => d.variable === "changeYearCVAPPerPoll");
    const baseYearCVAPPerPollLabel = baseYearCVAPPerPoll ? baseYearCVAPPerPoll.sizeDescr : '';

    return (
        <FilterGroup title="Per capita indicators">
            <ShowSlider
                k={`${geoLevel}-changeYearCVAPPerPoll`}
                label={changeYearCVAPPerPollLabel}
                disabled={disabled}
                toggle={showChangeYearCVAPPerPollCountyFilter}
                setToggle={setShowChangeYearCVAPPerPollCountyFilter}
                marks={changeYearCVAPPerPollCountyMarks}
                value={changeYearCVAPPerPollCountyFilter}
                setValue={setChangeYearCVAPPerPollCountyFilter}
            />
            <ShowSlider
                k={`${geoLevel}-baseYearCVAPPerPoll`}
                label={baseYearCVAPPerPollLabel}
                disabled={disabled}
                toggle={showBaseYearCVAPPerPollCountyFilter}
                setToggle={setShowBaseYearCVAPPerPollCountyFilter}
                marks={baseYearCVAPPerPollCountyMarks}
                value={baseYearCVAPPerPollCountyFilter}
                setValue={setBaseYearCVAPPerPollCountyFilter}
            />
        </FilterGroup>
    )
}

function SummaryIndicatorsFilter({geoLevel, disabled,
    changeNoPollsFilter, setChangeNoPollsFilter, changeNoPollsRangeMarks, showChangeNoPollsFilter, setShowChangeNoPollsFilter,
    overallChangeFilter, setOverallChangeFilter, overallChangeRangeMarks, showOverallChangeFilter, setShowOverallChangeFilter,
   }: 
   {geoLevel: string, disabled: boolean,
    changeNoPollsFilter: number[], setChangeNoPollsFilter: Function, changeNoPollsRangeMarks: any[],
    showChangeNoPollsFilter: boolean, setShowChangeNoPollsFilter: Function,
    overallChangeFilter: number[], setOverallChangeFilter: Function, overallChangeRangeMarks: any[],
    showOverallChangeFilter: boolean, setShowOverallChangeFilter: Function,
    }) {

    const overallChange = selectVariable.indicator.find((d: any) => d.variable === "changeNoPolls");
    const overallChangeLabel = overallChange ? overallChange.descr : '';

    const changeNoPolls = selectVariable.indicator.find((d: any) => d.variable === "changeNoPolls");
    const changeNoPollsLabel = changeNoPolls ? changeNoPolls.sizeDescr : '';

    return (
        <FilterGroup title="Summary indicators">
            <ShowSlider
                k={`${geoLevel}-changeNoPolls`}
                label={changeNoPollsLabel}
                disabled={disabled}
                toggle={showChangeNoPollsFilter}
                setToggle={setShowChangeNoPollsFilter}
                marks={changeNoPollsRangeMarks}
                value={changeNoPollsFilter}
                setValue={setChangeNoPollsFilter}
            />
            <ShowSlider
                k={`${geoLevel}-overallChange`}
                label={overallChangeLabel}
                disabled={disabled}
                toggle={showOverallChangeFilter}
                setToggle={setShowOverallChangeFilter}
                marks={overallChangeRangeMarks}
                value={overallChangeFilter}
                setValue={setOverallChangeFilter}
            />
        </FilterGroup>
    )
}


export function CountyFilters({
    geoFocus, pctBlackCVAPCountyFilter, setPctBlackCVAPCountyFilter, pctBlackCVAPRangeCountyMarks,
    showPctBlackCVAPCountyFilter, setShowPctBlackCVAPCountyFilter,
    pctHispanicCVAPCountyFilter, setPctHispanicCVAPCountyFilter, pctHispanicCVAPRangeCountyMarks,
    showPctHispanicCVAPCountyFilter, setShowPctHispanicCVAPCountyFilter,
    popDensityCountyFilter, setPopDensityCountyFilter, popDensityRangeCountyMarks,
    showPopDensityCountyFilter, setShowPopDensityCountyFilter,
    changeNoPollsCountyFilter, setChangeYearCVAPPerPollCountyFilter, changeNoPollsRangeCountyMarks, showChangeNoPollsCountyFilter, setShowChangeNoPollsCountyFilter,
    overallChangeCountyFilter, setOverallChangeCountyFilter, overallChangeRangeCountyMarks, showOverallChangeCountyFilter, setShowOverallChangeCountyFilter,
    changeYearCVAPPerPollCountyFilter, setChangeNoPollsCountyFilter, changeYearCVAPPerPollCountyMarks, showChangeYearCVAPPerPollCountyFilter, setShowChangeYearCVAPPerPollCountyFilter,
    baseYearCVAPPerPollCountyFilter, setBaseYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyMarks,  showBaseYearCVAPPerPollCountyFilter, setShowBaseYearCVAPPerPollCountyFilter
}: 
{   geoFocus: string, pctBlackCVAPCountyFilter: any[], setPctBlackCVAPCountyFilter: Function, pctBlackCVAPRangeCountyMarks: any[],
    showPctBlackCVAPCountyFilter: boolean, setShowPctBlackCVAPCountyFilter: Function,
    pctHispanicCVAPCountyFilter: any[], setPctHispanicCVAPCountyFilter: Function, pctHispanicCVAPRangeCountyMarks: any[],
    showPctHispanicCVAPCountyFilter: boolean, setShowPctHispanicCVAPCountyFilter: Function,
    popDensityCountyFilter: any[], setPopDensityCountyFilter: Function, popDensityRangeCountyMarks: any[],
    showPopDensityCountyFilter: boolean, setShowPopDensityCountyFilter: Function,
    changeNoPollsCountyFilter: number[], setChangeYearCVAPPerPollCountyFilter: Function, changeNoPollsRangeCountyMarks: any[],
    showChangeNoPollsCountyFilter: boolean, setShowChangeNoPollsCountyFilter: Function,
    overallChangeCountyFilter: number[], setOverallChangeCountyFilter: Function, overallChangeRangeCountyMarks: any[],
    showOverallChangeCountyFilter: boolean, setShowOverallChangeCountyFilter: Function,
    changeYearCVAPPerPollCountyFilter: number[], setChangeNoPollsCountyFilter: Function, changeYearCVAPPerPollCountyMarks: any[],
    showChangeYearCVAPPerPollCountyFilter: boolean, setShowChangeYearCVAPPerPollCountyFilter: Function,
    baseYearCVAPPerPollCountyFilter: number[], setBaseYearCVAPPerPollCountyFilter: Function, baseYearCVAPPerPollCountyMarks: any[],
    showBaseYearCVAPPerPollCountyFilter: boolean, setShowBaseYearCVAPPerPollCountyFilter: Function,
}) {

    let disabled = geoFocus === "County";

    return(
        <AccordionFilters title="County characteristics" defaultExpanded={true}>
            <SummaryIndicatorsFilter
                geoLevel="County"
                disabled={disabled}
                changeNoPollsFilter={changeNoPollsCountyFilter} setChangeNoPollsFilter={setChangeNoPollsCountyFilter} 
                changeNoPollsRangeMarks={changeNoPollsRangeCountyMarks} showChangeNoPollsFilter={showChangeNoPollsCountyFilter} setShowChangeNoPollsFilter={setShowChangeNoPollsCountyFilter}
                overallChangeFilter={overallChangeCountyFilter} setOverallChangeFilter={setOverallChangeCountyFilter} 
                overallChangeRangeMarks={overallChangeRangeCountyMarks} showOverallChangeFilter={showOverallChangeCountyFilter} setShowOverallChangeFilter={setShowOverallChangeCountyFilter}
            />
            <PerCapitaIndicatorsFilter 
                geoLevel="County"
                disabled={disabled}
                changeYearCVAPPerPollCountyFilter={changeYearCVAPPerPollCountyFilter} setChangeYearCVAPPerPollCountyFilter={setChangeYearCVAPPerPollCountyFilter}
                changeYearCVAPPerPollCountyMarks={changeYearCVAPPerPollCountyMarks} showChangeYearCVAPPerPollCountyFilter={showChangeYearCVAPPerPollCountyFilter} setShowChangeYearCVAPPerPollCountyFilter={setShowChangeYearCVAPPerPollCountyFilter}
                baseYearCVAPPerPollCountyFilter={baseYearCVAPPerPollCountyFilter} setBaseYearCVAPPerPollCountyFilter={setBaseYearCVAPPerPollCountyFilter}
                baseYearCVAPPerPollCountyMarks={baseYearCVAPPerPollCountyMarks} showBaseYearCVAPPerPollCountyFilter={showBaseYearCVAPPerPollCountyFilter} setShowBaseYearCVAPPerPollCountyFilter={setShowBaseYearCVAPPerPollCountyFilter} 
                />
            <EquityIndicatorsFilter 
                geoLevel="County"
                disabled={disabled}
                showPctBlackCVAPFilter={showPctBlackCVAPCountyFilter} 
                setShowPctBlackCVAPFilter={setShowPctBlackCVAPCountyFilter}
                pctBlackCVAPFilter={pctBlackCVAPCountyFilter}
                setPctBlackCVAPFilter={setPctBlackCVAPCountyFilter}
                pctBlackRangeMarks={pctBlackCVAPRangeCountyMarks}
                showPctHispanicCVAPFilter={showPctHispanicCVAPCountyFilter} 
                setShowPctHispanicCVAPFilter={setShowPctHispanicCVAPCountyFilter}
                pctHispanicCVAPFilter={pctHispanicCVAPCountyFilter}
                setPctHispanicCVAPFilter={setPctHispanicCVAPCountyFilter}
                pctHispanicRangeMarks={pctHispanicCVAPRangeCountyMarks}
                showPopDensityFilter={showPopDensityCountyFilter}
                setShowPopDensityFilter={setShowPopDensityCountyFilter}
                popDensityFilter={popDensityCountyFilter}
                setPopDensityFilter={setPopDensityCountyFilter}
                popDensityRangeMarks={popDensityRangeCountyMarks}
                />
        </AccordionFilters>  
    )
}

export function TractFilters(
    {pctBlackCVAPTractFilter, setPctBlackCVAPTractFilter, pctBlackCVAPRangeTractMarks,
    showPctBlackCVAPTractFilter, setShowPctBlackCVAPTractFilter,
    pctHispanicCVAPTractFilter, setPctHispanicCVAPTractFilter, pctHispanicCVAPRangeTractMarks,
    showPctHispanicCVAPTractFilter, setShowPctHispanicCVAPTractFilter,
    popDensityTractFilter, setPopDensityTractFilter, popDensityRangeTractMarks,
    showPopDensityTractFilter, setShowPopDensityTractFilter,

    changeNoPollsTractFilter, setChangeNoPollsTractFilter, changeNoPollsRangeTractMarks,
    showChangeNoPollsTractFilter, setShowChangeNoPollsTractFilter,
    overallChangeTractFilter, setOverallChangeTractFilter, overallChangeRangeTractMarks,
    showOverallChangeTractFilter, setShowOverallChangeTractFilter}: 
    {
    pctBlackCVAPTractFilter: any[], setPctBlackCVAPTractFilter: Function, pctBlackCVAPRangeTractMarks: any[],
    showPctBlackCVAPTractFilter: boolean, setShowPctBlackCVAPTractFilter: Function,
    pctHispanicCVAPTractFilter: any[], setPctHispanicCVAPTractFilter: Function, pctHispanicCVAPRangeTractMarks: any[],
    showPctHispanicCVAPTractFilter: boolean, setShowPctHispanicCVAPTractFilter: Function,
    popDensityTractFilter: any[], setPopDensityTractFilter: Function, popDensityRangeTractMarks: any[],
    showPopDensityTractFilter: boolean, setShowPopDensityTractFilter: Function,

    changeNoPollsTractFilter: number[], setChangeNoPollsTractFilter: Function, changeNoPollsRangeTractMarks: any[],
    showChangeNoPollsTractFilter: boolean, setShowChangeNoPollsTractFilter: Function,
    overallChangeTractFilter: number[], setOverallChangeTractFilter: Function, overallChangeRangeTractMarks: any[],
    showOverallChangeTractFilter: boolean, setShowOverallChangeTractFilter: Function
    }) {

    return(
        <AccordionFilters title="Tract characteristics" defaultExpanded={true}>
             <SummaryIndicatorsFilter
                geoLevel="Tract"
                disabled={false}
                changeNoPollsFilter={changeNoPollsTractFilter} setChangeNoPollsFilter={setChangeNoPollsTractFilter} 
                changeNoPollsRangeMarks={changeNoPollsRangeTractMarks} showChangeNoPollsFilter={showChangeNoPollsTractFilter} setShowChangeNoPollsFilter={setShowChangeNoPollsTractFilter}
                overallChangeFilter={overallChangeTractFilter} setOverallChangeFilter={setOverallChangeTractFilter} 
                overallChangeRangeMarks={overallChangeRangeTractMarks} showOverallChangeFilter={showOverallChangeTractFilter} setShowOverallChangeFilter={setShowOverallChangeTractFilter}
            />
            <EquityIndicatorsFilter 
                geoLevel="Tract"
                disabled={false}
                showPctBlackCVAPFilter={showPctBlackCVAPTractFilter} 
                setShowPctBlackCVAPFilter={setShowPctBlackCVAPTractFilter}
                pctBlackCVAPFilter={pctBlackCVAPTractFilter}
                setPctBlackCVAPFilter={setPctBlackCVAPTractFilter}
                pctBlackRangeMarks={pctBlackCVAPRangeTractMarks}
                showPctHispanicCVAPFilter={showPctHispanicCVAPTractFilter} 
                setShowPctHispanicCVAPFilter={setShowPctHispanicCVAPTractFilter}
                pctHispanicCVAPFilter={pctHispanicCVAPTractFilter}
                setPctHispanicCVAPFilter={setPctHispanicCVAPTractFilter}
                pctHispanicRangeMarks={pctHispanicCVAPRangeTractMarks}
                showPopDensityFilter={showPopDensityTractFilter}
                setShowPopDensityFilter={setShowPopDensityTractFilter}
                popDensityFilter={popDensityTractFilter}
                setPopDensityFilter={setPopDensityTractFilter}
                popDensityRangeMarks={popDensityRangeTractMarks}
                />
        </AccordionFilters>
    )
}

export function filterSelected(feature: any, measureName: string, range: number[], selectedState: any, pollSummary?: boolean) {

    if (feature.properties!.changeYearData) {
        if (feature.properties!.stfp === selectedState.stfp) {

            if (pollSummary) {
                let pollMeasure = feature.properties!.changeYearData.pollSummary.status[measureName];
                return pollMeasure >= range[0] && pollMeasure <= range[1];
            } else {
                let equityMeasure = feature.properties!.changeYearData[measureName].equityMeasure;
                return equityMeasure >= range[0] && equityMeasure <= range[1];
            }
        } else {
            return feature.properties!.selected;
        }
    } else {
        return feature.properties!.selected;
    }
}

export function setFiltersRange(df: any[], setMarks: Function, setFilterValues: Function, 
    measureName: string, pollSummary: boolean, percent: boolean, step: number = 1) {
    
    let range;

    if (pollSummary) {
        range = d3.extent(df.map((d: any) => d.properties.changeYearData.pollSummary.status[measureName]) || []) as number[];
    } else {
        range = d3.extent(df.map((d: any) => d.properties.changeYearData[measureName].equityMeasure) || []) as number[];
    }
 
    let min = Math.floor(range[0]/step)*step;
    let max = Math.ceil(range[1]/step)*step;

    setMarks([
        {value: min, label: percent ? `${min}%`: min},
        {value: max, label: percent ? `${max}%`: max},
        ]);
    
    setFilterValues([min, max]);
}